/**
 * Cores do app escolhidas pela loja no gestor (PRAT-154).
 *
 * A loja define a cor principal (botões, preços, destaques) e a secundária
 * (botões escuros, barra do carrinho). Só essas cores mudam o app: nada é
 * tirado da logo ou da capa. A cor do texto sobre cada uma é calculada aqui
 * (branco ou quase preto, o que tiver mais contraste), para o botão continuar
 * legível com qualquer cor que a loja escolher.
 *
 * As cores são escritas nas CSS variables que o tema já usa (`index.css`) e
 * guardadas por loja no aparelho, para a próxima abertura já pintar certo
 * antes da API responder.
 */

export type StoreAppTheme = {
  primary_color?: string | null;
  secondary_color?: string | null;
};

const STORAGE_PREFIX = 'pratigo-store-theme:';
const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#1a1a1a';

/** Variáveis que o tema da loja pode sobrescrever (lista fechada). */
const THEME_VARIABLES = [
  '--primary',
  '--primary-foreground',
  '--muted-primary',
  '--peach-blossom',
  '--secondary',
  '--secondary-foreground',
] as const;

const HEX_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Hex `#rgb`/`#rrggbb` em minúsculas e com 6 dígitos; qualquer outra coisa vira `null`. */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  const hex = trimmed.slice(1).toLowerCase();
  return hex.length === 3
    ? `#${hex
        .split('')
        .map(c => c + c)
        .join('')}`
    : `#${hex}`;
}

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb
    .map(c =>
      Math.round(Math.min(255, Math.max(0, c)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razão de contraste WCAG entre duas cores hex. */
export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Texto (branco ou escuro) com mais contraste sobre a cor dada. */
export function readableTextOn(background: string): string {
  return contrastRatio(background, LIGHT_TEXT) >= contrastRatio(background, DARK_TEXT)
    ? LIGHT_TEXT
    : DARK_TEXT;
}

/** Mistura `color` com `target` (0 = só color, 1 = só target). */
function mix(color: string, target: string, amount: number): string {
  const a = toRgb(color);
  const b = toRgb(target);
  return toHex([0, 1, 2].map(i => a[i] + (b[i] - a[i]) * amount) as [number, number, number]);
}

/** Valores das CSS variables para o tema; só entram as cores válidas. */
export function buildThemeVariables(
  theme: StoreAppTheme | null | undefined
): Record<string, string> {
  const vars: Record<string, string> = {};
  const primary = normalizeHexColor(theme?.primary_color);
  const secondary = normalizeHexColor(theme?.secondary_color);

  if (primary) {
    vars['--primary'] = primary;
    vars['--primary-foreground'] = readableTextOn(primary);
    // Tom escuro para texto sobre o fundo claro de destaque, e o fundo claro.
    vars['--muted-primary'] = mix(primary, '#000000', 0.5);
    vars['--peach-blossom'] = mix(primary, '#ffffff', 0.9);
  }
  if (secondary) {
    vars['--secondary'] = secondary;
    vars['--secondary-foreground'] = readableTextOn(secondary);
  }
  return vars;
}

function root(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.documentElement;
}

/** Aplica o tema no `<html>`; cores ausentes voltam ao padrão pratiGO. */
export function applyStoreTheme(theme: StoreAppTheme | null | undefined): void {
  const el = root();
  if (!el) return;
  const vars = buildThemeVariables(theme);
  THEME_VARIABLES.forEach(name => {
    if (vars[name]) el.style.setProperty(name, vars[name]);
    else el.style.removeProperty(name);
  });
}

export function resetStoreTheme(): void {
  applyStoreTheme(null);
}

export function readCachedStoreTheme(slug: string | null | undefined): StoreAppTheme | null {
  if (!slug) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + slug);
    return raw ? (JSON.parse(raw) as StoreAppTheme) : null;
  } catch {
    return null;
  }
}

/** Guarda o tema que a API devolveu para a loja (null apaga). */
export function cacheStoreTheme(slug: string, theme: StoreAppTheme | null | undefined): void {
  try {
    if (
      theme &&
      (normalizeHexColor(theme.primary_color) || normalizeHexColor(theme.secondary_color))
    ) {
      localStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify(theme));
    } else {
      localStorage.removeItem(STORAGE_PREFIX + slug);
    }
  } catch {
    // Sem storage (aba anônima): o tema vale só enquanto a página está aberta.
  }
}

/** Rotas do app que não são de loja: o primeiro trecho delas não é um slug. */
const NON_STORE_SEGMENTS = new Set([
  'auth',
  'login-by-link',
  'entrar',
  'search',
  'profile',
  'indicacao',
  'orders',
  'cart',
  'sentry-test',
  'error-boundary-test',
]);

/**
 * Loja em que o cliente está: o slug da URL, ou a última loja aberta nesta aba
 * (pedidos, carrinho e perfil continuam com as cores dela). Na lista de lojas
 * (`/`) não há loja: vale o tema padrão.
 */
export function currentStoreSlug(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) return null;
  if (!NON_STORE_SEGMENTS.has(first)) return decodeURIComponent(first);
  try {
    const stored = sessionStorage.getItem('store-slug');
    return stored && stored !== 'undefined' ? stored : null;
  } catch {
    return null;
  }
}

export const STORE_THEME_EVENT = 'pratigo:store-theme';

/** Grava o tema vindo da API e repinta se for a loja aberta agora. */
export function rememberStoreTheme(slug: string, theme: StoreAppTheme | null | undefined): void {
  cacheStoreTheme(slug, theme);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(STORE_THEME_EVENT, { detail: { slug } }));
  }
}

/** Aplica o tema guardado da loja da URL atual (antes do primeiro render). */
export function applyThemeForLocation(pathname: string): void {
  applyStoreTheme(readCachedStoreTheme(currentStoreSlug(pathname)));
}
